import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatCurrency,
  formatDate,
  formatTime,
  formatNumber,
  type CurrencyFormatOptions,
  type DateFormatOptions,
  type NumberFormatOptions,
} from '@zap/shared-logic';

export interface UseFormattersOptions {
  /** ISO 4217 currency code from the venue. Defaults to USD. */
  currency?: string;
}

export function useFormatters({ currency = 'USD' }: UseFormattersOptions = {}) {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'en';

  return useMemo(
    () => ({
      locale,
      currency,
      formatCurrency: (amount: number, opts: Omit<CurrencyFormatOptions, 'locale' | 'currency'> = {}) =>
        formatCurrency(amount, { locale, currency, ...opts }),
      formatDate: (date: Date | string | number, opts: Omit<DateFormatOptions, 'locale'> = {}) =>
        formatDate(date, { locale, ...opts }),
      formatTime: (date: Date | string | number, opts: Omit<DateFormatOptions, 'locale'> = {}) =>
        formatTime(date, { locale, ...opts }),
      formatNumber: (value: number, opts: Omit<NumberFormatOptions, 'locale'> = {}) =>
        formatNumber(value, { locale, ...opts }),
    }),
    [locale, currency],
  );
}
